import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  MenuItem,
  Select,
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import { Breadcrumb, CTSinput, StyledTableCell, StyledTableRow } from "../../reusableComponent/Reusables";
import SaveIcon from "@mui/icons-material/Save";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import AddIcon from "@mui/icons-material/Add";
import styles from "../../../styles.module.css";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import productionentryService from "../../../services/productionentry.Service";
import ReusableSnackbar from "../../reusableComponent/ReusableSnackbar";
import dayjs from "dayjs";
import driverMasterService from "../../../services/masters/driverMaster.Service";
import vehicleMasterService from "../../../services/masters/vehicleMaster.Service";
import inwardService from "../../../services/inward.Service";
import driveractivityplanning from "../../../services/driveractivityplanning";

const DriverActivityPlanning = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors }, setValue } = useForm();
  const location = useLocation();

  // User info from session storage
  const login_details = sessionStorage.getItem("loginUser");
  const userD = JSON.parse(login_details);
  const userInfo = userD.result.userInfo;
  const unit_id = userInfo.depot_id || userInfo.plant_id;
  const depot_id = userInfo.depot_id;
  const depot_name = userInfo.depot_name || userInfo.plant_name || ""; // Fetch depot_name
  const short_code = userInfo.depot_short_code || userInfo.plant_short_code;

  // Edit mode handling
  const editInfo = location.state;
  const isEditMode = editInfo && typeof editInfo === "object" && editInfo.entry_id;
  const editData = isEditMode ? editInfo : null;
  const editId = editData ? editData.entry_id : null;

  // State
  const [data, setData] = useState({
    entry_no: editData ? editData.entry_no : "",
    start_time: editData ? editData.start_time : "",
    end_time: editData ? editData.end_time : "",
    depot: editData ? editData.depot : depot_name, // Use depot_name
    depot_id: editData ? editData.depot_id : depot_id || "",
    depot_name: editData ? editData.depot_name : depot_name || "", // Add depot_name
    driver_name: editData ? editData.driver_name : "",
    driver_id: editData ? editData.driver_id : "", // Add driver_id
    vehicle_id: editData ? editData.vehicle_id : "", // Add vehicle_id
    entry_date: editData?.entry_date
      ? dayjs(editData.entry_date).isValid()
        ? dayjs(editData.entry_date).format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD"),
    vehicle_no: editData ? editData.vehicle_no : "",
    start_km: editData ? editData.start_km : "",
    end_km: editData ? editData.end_km : "",
    modified_at: dayjs().format("YYYY-MM-DD HH:mm:ss"),
    modified_by: editData ? userInfo.user_id : null,
    created_at: dayjs().format("YYYY-MM-DD HH:mm:ss"),
    created_by: editData ? editData.created_by : userInfo.user_id,
  });

  const [tabledetails, settabledetails] = useState(() => {
    if (isEditMode && editData && editData.table_details) {
      try {
        return JSON.parse(editData.table_details).map((row, index) => ({
          sr_no: index + 1,
          customer_name: row.customer_name || "",
          challan_no: row.challan_no || "",
          activity: row.activity || "",
        }));
      } catch (error) {
        console.error("Error parsing table_details:", error);
        return [];
      }
    }
    return [];
  });

  const [successMessage, setSuccessMessage] = useState(null);
  const [severity, setSeverity] = useState("info");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [driver, setDriver] = useState([]);
  const [vehicle, setVehicle] = useState([]);
  const [customer, setCustomer] = useState([]);
  const [challanNumbers, setChallanNumbers] = useState({});

  const activityOptions = [
    { value: "Delivery Collection", label: "Delivery Collection" },
    { value: "Delivery", label: "Delivery" },
    { value: "Empty Collection", label: "Empty Collection" },
  ];

  // Set form values for react-hook-form
  useEffect(() => {
    Object.keys(data).forEach((key) => {
      setValue(key, data[key]);
    });
  }, [data, setValue]);

  // Fetch initial data
  useEffect(() => {
    async function fetchData() {
      try {
        const [driverData, vehicleData, customerData] = await Promise.all([
          driverMasterService.getDriverList({ page: 0, size: 999, searchKey: "" }),
          vehicleMasterService.getVehicleList({ page: 0, size: 999, searchKey: "" }),
          inwardService.getInwardCustomerList(depot_id),
        ]);
        setDriver(driverData);
        setVehicle(vehicleData);
        setCustomer(customerData);
      } catch (error) {
        console.error("Data fetching failed:", error);
        setSuccessMessage("Error fetching data");
        setSeverity("error");
        setOpenSnackbar(true);
      }
    }
    fetchData();
  }, [depot_id]);

  // Fetch challan numbers when customer changes
  const fetchChallanNumbers = async (cust_id, index) => {
    try {
      const challanData = await driveractivityplanning.getactivitychallannoList({ cust_id });
      setChallanNumbers((prev) => ({
        ...prev,
        [index]: challanData.map((item) => item.entry_no),
      }));
    } catch (error) {
      console.error("Error fetching challan numbers:", error);
      setSuccessMessage("Error fetching challan numbers");
      setSeverity("error");
      setOpenSnackbar(true);
    }
  };

  // Handle form input changes
  const handleChange = ({ target }) => {
    const { name, value } = target;
    setData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setValue(name, value); // Update react-hook-form
  };

  // Handle driver selection
  const handleDriverChange = (event, newValue) => {
    const selectedDriver = driver.find((d) => d.driver_name === newValue);
    setData((prevData) => ({
      ...prevData,
      driver_name: newValue || "",
      driver_id: selectedDriver ? selectedDriver.driver_id : "", // Store driver_id
    }));
    setValue("driver_name", newValue || "");
    setValue("driver_id", selectedDriver ? selectedDriver.driver_id : "");
  };

  // Handle vehicle selection
  const handleVehicleChange = (event, newValue) => {
    const selectedVehicle = vehicle.find((v) => v.vehicle_no === newValue);
    setData((prevData) => ({
      ...prevData,
      vehicle_no: newValue || "",
      vehicle_id: selectedVehicle ? selectedVehicle.vehicle_id : "", // Store vehicle_id
    }));
    setValue("vehicle_no", newValue || "");
    setValue("vehicle_id", selectedVehicle ? selectedVehicle.vehicle_id : "");
  };

  // Handle table input changes
  const handleChangeInput = (e, index) => {
    const { name, value } = e.target;
    settabledetails((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [name]: value, sr_no: index + 1 };
      return updated;
    });
  };

  // Handle customer selection and fetch challan numbers
  const handleCustomerChange = (event, newValue, index) => {
    const selectedCustomer = customer.find((cust) => cust.cust_name === newValue);
    const cust_id = selectedCustomer ? selectedCustomer.cust_id : null;

    settabledetails((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        customer_name: newValue || "",
        challan_no: "",
        sr_no: index + 1,
      };
      return updated;
    });

    if (cust_id) {
      fetchChallanNumbers(cust_id, index);
    } else {
      setChallanNumbers((prev) => ({
        ...prev,
        [index]: [],
      }));
    }
  };

  // Add new table row
  const handleAddProductionEntryFields = () => {
    settabledetails((prev) => [
      ...prev,
      {
        sr_no: prev.length + 1,
        customer_name: "",
        challan_no: "",
        activity: "",
      },
    ]);
  };

  // Remove table row
  const handleRemoveProductionEntrydetails = (index) => {
    settabledetails((prev) => prev.filter((_, i) => i !== index));
    setChallanNumbers((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  // Handle form submission
  const onSubmit = async (formData) => {
    console.log("Form Data:", formData);
    console.log("Child Details:", tabledetails);

    if (!formData.depot || !formData.driver_name || !formData.vehicle_no || !formData.entry_date) {
      setSuccessMessage("Please fill all required fields");
      setSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    const validRows = tabledetails.filter(
      (row) => row.customer_name && row.challan_no && row.activity
    );
    if (validRows.length === 0) {
      setSuccessMessage("Please add at least one valid row with customer, challan, and activity");
      setSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    const deliveryData = {
      ...formData,
      entry_id: editId,
      depot_name: data.depot_name, // Include depot_name
      driver_id: data.driver_id, // Include driver_id
      vehicle_id: data.vehicle_id, // Include vehicle_id
      table_details: JSON.stringify(validRows),
      changedUpdatedValue: isEditMode ? "edit" : "add",
      unit_id,
      short_code,
    };

    try {
      if (isEditMode) {
        const response = await productionentryService.editDeleteProductionEntry(deliveryData);
        console.log("Edit Response:", response);
        setSuccessMessage("Updated successfully");
      } else {
        const response = await driveractivityplanning.adddriveractivity(deliveryData);
        console.log("Add Response:", response);
        setSuccessMessage("Added successfully");
      }
      setSeverity("success");
      setOpenSnackbar(true);
      setTimeout(() => {
        setOpenSnackbar(false);
        navigate(-1);
      }, 3000);
    } catch (error) {
      console.error("Submission Error:", error);
      setSuccessMessage(`Error occurred during submission: ${error.message}`);
      setSeverity("error");
      setOpenSnackbar(true);
      setTimeout(() => setOpenSnackbar(false), 5000);
    }
  };

  // Close snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setOpenSnackbar(false);
    setSuccessMessage(null);
  };

  // Cancel button
  const CancelButton = () => (
    <Button id={styles.btn_cancel} onClick={() => navigate(-1)}>
      <Typography className={styles.btn_cancel_txt}>Cancel</Typography>
    </Button>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box className={styles.cts_page}>
        <Box className={styles.body}>
          <Breadcrumb routeSegments={[{ name: "Driver Activity Planning" }]} />
          <Box sx={{ display: "flex" }}>
            <Grid container spacing={2} sx={{ pt: 1.5, pb: 1.5 }}>
              <Grid item md={7} sm={6} xs={12}>
                <Typography className={styles.header_txt}>
                  DRIVER ACTIVITY PLANNING
                </Typography>
              </Grid>
            </Grid>
          </Box>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ justifyContent: "flex-end", display: "flex" }}>
              {!isEditMode && (
                <Grid item xs={12}>
                  <Typography className={styles.lastEntry_Txt}>
                    Last Entry No: {data.entry_no || 0}
                  </Typography>
                </Grid>
              )}
            </Box>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <Grid container spacing={0.6}>
                  <Grid item xs={10} sm={10}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                      <Typography className={styles.label_Txt}>Depot</Typography>
                      <CTSinput
                        size="small"
                        fullWidth
                        name="depot"
                        value={data.depot}
                        {...register("depot", { required: true })}
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          className: styles.CTSinput_Box,
                          readOnly: true,
                          style: { backgroundColor: "#f0f0f0" },
                        }}
                        error={!!errors.depot}
                        helperText={errors.depot ? "Depot is required" : ""}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={10} sm={10}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                      <Typography className={styles.label_Txt}>
                        Driver Name 
                      </Typography>
                      <Autocomplete
                        freeSolo
                        options={driver.map((item) => item.driver_name)}
                        value={data.driver_name || ""}
                        onChange={handleDriverChange}
                        onInputChange={(event, newInputValue) => {
                          handleDriverChange(event, newInputValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            {...register("driver_name", { required: true })}
                            placeholder="Select or type driver"
                            size="small"
                            variant="outlined"
                            required
                            InputProps={{
                              ...params.InputProps,
                              className: styles.CTSinput_Box,
                            }}
                            error={!!errors.driver_name}
                            helperText={errors.driver_name ? "Driver name is required" : ""}
                          />
                        )}
                        sx={{ width: "100%" }}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={10} sm={10}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                      <Typography className={styles.label_Txt}>
                        Vehicle No
                      </Typography>
                      <Autocomplete
                        freeSolo
                        options={vehicle.map((item) => item.vehicle_no)}
                        value={data.vehicle_no || ""}
                        onChange={handleVehicleChange}
                        onInputChange={(event, newInputValue) => {
                          handleVehicleChange(event, newInputValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            {...register("vehicle_no", { required: true })}
                            placeholder="Select or type vehicle"
                            size="small"
                            variant="outlined"
                            required
                            InputProps={{
                              ...params.InputProps,
                              className: styles.CTSinput_Box,
                            }}
                            error={!!errors.vehicle_no}
                            helperText={errors.vehicle_no ? "Vehicle no is required" : ""}
                          />
                        )}
                        sx={{ width: "100%" }}
                      />
                    </Box>
                  </Grid>
                  {/* <Grid item xs={10} sm={10}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                      <Typography className={styles.label_Txt}>
                        Vehicle No 
                      </Typography>
                      <Autocomplete
                        options={vehicle.map((item) => item.vehicle_no)}
                        value={data.vehicle_no || ""}
                        onChange={handleVehicleChange}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            {...register("vehicle_no", { required: true })}
                            placeholder="Select vehicle number"
                            size="small"
                            variant="outlined"
                            required
                            InputProps={{
                              ...params.InputProps,
                              className: styles.CTSinput_Box,
                            }}
                            error={!!errors.vehicle_no}
                            helperText={errors.vehicle_no ? "Vehicle number is required" : ""}
                          />
                        )}
                        sx={{ width: "100%" }}
                      />
                    </Box>
                  </Grid> */}
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Grid container spacing={0.6}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                      <Typography className={styles.label_Txt}>Entry No</Typography>
                      <CTSinput
                        size="small"
                        fullWidth
                        value={data.entry_no}
                        {...register("entry_no")}
                        variant="outlined"
                        InputProps={{
                          readOnly: true,
                          className: styles.CTSinput_Box,
                          style: { backgroundColor: "#f0f0f0" },
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
                      <Typography className={styles.label_Txt}>Entry Date</Typography>
                      <DatePicker
                        format="DD/MM/YYYY"
                        value={data.entry_date ? dayjs(data.entry_date) : null}
                        onChange={(newValue) => {
                          const formattedDate = newValue ? newValue.format("YYYY-MM-DD") : "";
                          setData((prev) => ({
                            ...prev,
                            entry_date: formattedDate,
                          }));
                          setValue("entry_date", formattedDate);
                        }}
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                            required: true,
                            InputProps: {
                              className: styles.CTSinput_Box,
                              disableUnderline: true,
                            },
                            ...register("entry_date", { required: true }),
                            error: !!errors.entry_date,
                            helperText: errors.entry_date ? "Entry date is required" : "",
                          },
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                      <Typography className={styles.label_Txt}>Start KM</Typography>
                      <CTSinput
                        size="small"
                        fullWidth
                        name="start_km"
                        value={data.start_km}
                        {...register("start_km")}
                        onChange={handleChange}
                        variant="outlined"
                        InputProps={{ className: styles.CTSinput_Box }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                      <Typography className={styles.label_Txt}>End KM</Typography>
                      <CTSinput
                        size="small"
                        fullWidth
                        name="end_km"
                        value={data.end_km}
                        {...register("end_km")}
                        onChange={handleChange}
                        variant="outlined"
                        InputProps={{ className: styles.CTSinput_Box }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                      <Typography className={styles.label_Txt}>Start Time</Typography>
                      <CTSinput
                        size="small"
                        fullWidth
                        type="time"
                        name="start_time"
                        value={data.start_time}
                        {...register("start_time")}
                        onChange={handleChange}
                        InputProps={{ className: styles.CTSinput_Box }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                      <Typography className={styles.label_Txt}>End Time</Typography>
                      <CTSinput
                        size="small"
                        fullWidth
                        type="time"
                        name="end_time"
                        value={data.end_time}
                        {...register("end_time")}
                        onChange={handleChange}
                        InputProps={{ className: styles.CTSinput_Box }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Paper sx={{ width: "100%" }}>
                  <TableContainer component={Paper} className={styles.list_tbl_container}>
                    <Table size="small" stickyHeader>
                      <TableHead className={styles.table_head_container}>
                        <StyledTableRow>
                          <StyledTableCell align="center" className={styles.table_head} sx={{ width: "10%" }}>
                            Sr No
                          </StyledTableCell>
                          <StyledTableCell align="left" className={styles.table_head}>
                            Customer Name
                          </StyledTableCell>
                          <StyledTableCell align="left" className={styles.table_head}>
                            Challan No
                          </StyledTableCell>
                          <StyledTableCell align="left" className={styles.table_head}>
                            Activity
                          </StyledTableCell>
                          <StyledTableCell align="center" className={styles.table_head}>
                            Action
                          </StyledTableCell>
                        </StyledTableRow>
                      </TableHead>
                      <TableBody>
                        {tabledetails.map((row, index) => (
                          <StyledTableRow key={index}>
                            <StyledTableCell align="center" sx={{ width: "10%" }}>
                              {row.sr_no}
                            </StyledTableCell>
                            <StyledTableCell align="left" sx={{ width: "38%" }}>
                              <Autocomplete
                                freeSolo
                                options={customer.map((cust) => cust.cust_name)}
                                value={row.customer_name || ""}
                                onChange={(event, newValue) => handleCustomerChange(event, newValue, index)}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    InputProps={{
                                      ...params.InputProps,
                                      className: styles.CTSinput_Box,
                                    }}
                                  />
                                )}
                              />
                            </StyledTableCell>
                            <StyledTableCell align="left">
                              <Autocomplete
                                freeSolo
                                options={challanNumbers[index] || []}
                                value={row.challan_no || ""}
                                onChange={(event, newValue) => {
                                  settabledetails((prev) => {
                                    const updated = [...prev];
                                    updated[index] = {
                                      ...updated[index],
                                      challan_no: newValue || "",
                                      sr_no: index + 1,
                                    };
                                    return updated;
                                  });
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    InputProps={{
                                      ...params.InputProps,
                                      className: styles.CTSinput_Box,
                                    }}
                                  />
                                )}
                              />
                            </StyledTableCell>
                            <StyledTableCell align="left">
                              <Select
                                size="small"
                                name="activity"
                                value={row.activity || ""}
                                onChange={(e) => handleChangeInput(e, index)}
                                variant="outlined"
                                fullWidth
                                displayEmpty
                                renderValue={(selected) => (selected ? selected : "Select Activity")}
                                sx={{ backgroundColor: "#fff" }}
                              >
                                {activityOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              <Button
                                color="error"
                                onClick={() => handleRemoveProductionEntrydetails(index)}
                              >
                                <DeleteForeverIcon />
                              </Button>
                            </StyledTableCell>
                          </StyledTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
                    <Button
                      onClick={handleAddProductionEntryFields}
                      startIcon={<AddIcon />}
                      sx={{ color: "#1976d2" }}
                    >
                      Add Row
                    </Button>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} className={styles.grid_input}>
                <Box className={styles.btn_container}>
                  <Button id={styles.btn_save} type="submit">
                    <SaveIcon className={styles.btn_cancel_txt} />
                    <Typography className={styles.btn_cancel_txt}>
                      {isEditMode ? "Update" : "Save"}
                    </Typography>
                  </Button>
                  <CancelButton />
                </Box>
              </Grid>
            </Grid>
          </form>
          <ReusableSnackbar
            open={openSnackbar}
            onClose={handleCloseSnackbar}
            message={successMessage}
            severity={severity}
            autoHideDuration={3000}
            vertical="top"
            horizontal="right"
          />
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default DriverActivityPlanning;